import React, {
  FC,
  useMemo,
  ReactNode,
  memo,
  useCallback,
}                                   from 'react';
import { History }                  from 'history';
import {
  ITotalInnerStats,
  IMonthStats,
  IServiceStats,
  IDashLocationState,
}                                   from '@typings/dashboard';
import {
  TDataTableRow,
  IDataTableCell,
}                                   from '@typings/components';
import {
  EStatsService,
  EStatsInner,
  EStatsType,
  EDashboardPage,
}                                   from '@enums/dashboard';
import { EAppPage }                 from '@enums/common';
import { useToggle }                from '@utils/useToggle';
import { formatCurrency }           from '@utils/commonFunctions';
import {
  useFetchServicesStatistics,
  useFetchInnerMonthStatistics,
  useFetchInnerTotalStatistics,
  useDownloadStatisticsExcel,
}                                   from '@modules/dashboard/queries/dashboardQueries';
import {
  SERVICE_STATS_TYPES_ORDER,
  INNER_STATS_TYPES_ORDER,
}                                   from '@modules/dashboard/utils/suggestions';
import { MONTH_FULLNAME_BY_NUMBER } from '@utils/suggestions';
import { StatisticsBlock }          from '@modules/dashboard/components/StatisticsBlock';

type TStatsType = EStatsService | EStatsInner;

interface ITotalRowsData {
  value         : number;
  withCurrency? : boolean;
}

const getStatsTableValue = (
  value?       : number,
  withCurrency = false,
): ReactNode => {
  if (!value || !value) {
    return '-';
  }

  return withCurrency ? formatCurrency(value) : value;
};

const getMonthlyStatsTableRow = (
  { year, month, services } : IMonthStats,
  statsTypesOrder           : TStatsType[],
  isInnerStats              = false,
): TDataTableRow => {
  const periodCell: IDataTableCell = {
    value : `${MONTH_FULLNAME_BY_NUMBER[month]} ${year}`,
    align : 'left',
  };

  const statsCells: TDataTableRow = statsTypesOrder.reduce(
    (acc: TDataTableRow, curServiceType: TStatsType): TDataTableRow => {
      const currServiceStats: IServiceStats<TStatsType> | undefined = services.find(
        ({ serviceType }) => serviceType === curServiceType,
      );

      const withCurrency: boolean = isInnerStats && curServiceType === EStatsInner.InvoiceAmount;

      return [
        ...acc,
        {
          value : getStatsTableValue(currServiceStats?.count, withCurrency),
          align : 'center',
        },
      ];
    },
    [],
  );

  return [periodCell, ...statsCells];
};

const getTotalStatsTableRows = ({
  adsStatistic,
  auctionStatistic,
  invoiceStatistic,
  vinStatistic,
}: ITotalInnerStats): TDataTableRow[] => {
  const orderedData: ITotalRowsData[] = [
    { value: vinStatistic.successfulLogsCount },
    { value: vinStatistic.unsuccessfulLogsCount },
    { value: adsStatistic.allAdsCount },
    { value: adsStatistic.activeAdsCount },
    { value: invoiceStatistic.currentDue,      withCurrency: true },
    { value: invoiceStatistic.pastDue,         withCurrency: true },
    { value: invoiceStatistic.currentEstimate, withCurrency: true },
    { value: auctionStatistic.totalActiveBidsCount },
    { value: auctionStatistic.activeAuctionsCount },
    { value: auctionStatistic.activeUsersCount },
    { value: auctionStatistic.activeAuctionViewsCount },
  ];

  const row: TDataTableRow = orderedData.map(({ value, withCurrency }) => ({
    align : 'center',
    value : getStatsTableValue(value, withCurrency),
  }));

  return [row];
};

type TProps = Pick<History<IDashLocationState>, 'push'> & IDashLocationState

const Statistics: FC<TProps> = ({ cuid, cuName, push }) => {
  const [isServiceStatsExpanded, toggleIsServiceStatsExpanded] = useToggle();
  const [isInnerStatsExpanded, toggleIsInnerStatsExpanded]     = useToggle();

  // #region API INTEGRATION
  const { data: servicesStatistics } = useFetchServicesStatistics(cuid);

  const { data: innerMonthStatistics } = useFetchInnerMonthStatistics(cuid);

  const { data: innerTotalStatistics } = useFetchInnerTotalStatistics(cuid);

  const { mutateAsync: downloadExcel } = useDownloadStatisticsExcel();
  // #endregion

  const isLoadingStats = useMemo<boolean>(
    () => !servicesStatistics || !innerMonthStatistics || !innerTotalStatistics,
    [innerMonthStatistics, innerTotalStatistics, servicesStatistics],
  );

  const servicesStatisticsRows = useMemo<TDataTableRow[]>(
    () => (!servicesStatistics
      ? []
      : servicesStatistics.map((item) => getMonthlyStatsTableRow(item, SERVICE_STATS_TYPES_ORDER))),
    [servicesStatistics],
  );

  const innerMonthStatisticsRows = useMemo<TDataTableRow[]>(
    () => (!innerMonthStatistics
      ? []
      : innerMonthStatistics.map((item) => getMonthlyStatsTableRow(
        item,
        INNER_STATS_TYPES_ORDER,
        true,
      ))),
    [innerMonthStatistics],
  );

  const innerTotalStatisticsRows = useMemo<TDataTableRow[]>(
    () => (!innerTotalStatistics ? [] : getTotalStatsTableRows(innerTotalStatistics)),
    [innerTotalStatistics],
  );

  const downloadServicesExcel = useCallback(
    async (): Promise<void> => {
      await downloadExcel({
        statsType: EStatsType.Services,
        cuid,
      });
    },
    [cuid, downloadExcel],
  );

  const downloadInnerMonthExcel = useCallback(
    async (): Promise<void> => {
      await downloadExcel({
        statsType: EStatsType.InnerMonthly,
        cuid,
      });
    },
    [cuid, downloadExcel],
  );

  const downloadInnerTotalExcel = useCallback(
    async (): Promise<void> => {
      await downloadExcel({
        statsType: EStatsType.InnerTotal,
        cuid,
      });
    },
    [cuid, downloadExcel],
  );

  const onVinHistoryLinkClick = useCallback(
    () => {
      push({
        pathname : `${EAppPage.Dashboard}/${EDashboardPage.VinDecodeHistory}`,
        state    : {
          cuid   : cuid || null,
          cuName : cuName || null,
        },
      });
    },
    [cuName, cuid, push],
  );

  return (
    <StatisticsBlock
      isLoadingStats               = {isLoadingStats}
      servicesStatisticsRows       = {servicesStatisticsRows}
      innerMonthStatisticsRows     = {innerMonthStatisticsRows}
      innerTotalStatisticsRows     = {innerTotalStatisticsRows}
      isServiceStatsExpanded       = {isServiceStatsExpanded}
      isInnerStatsExpanded         = {isInnerStatsExpanded}
      toggleIsServiceStatsExpanded = {toggleIsServiceStatsExpanded}
      toggleIsInnerStatsExpanded   = {toggleIsInnerStatsExpanded}
      downloadServicesExcel        = {downloadServicesExcel}
      downloadInnerMonthExcel      = {downloadInnerMonthExcel}
      downloadInnerTotalExcel      = {downloadInnerTotalExcel}
      onVinHistoryLinkClick        = {onVinHistoryLinkClick}
    />
  );
};

export const StatisticsContainer = memo(Statistics);
